<template>
  <div class="container" id="user-profile-page">
    <div class="row">
      <div class="col-12 mb-3">
        <div class="card mt-3">
          <h5 class="card-header d-flex justify-content-between align-items-center">
            <div>
              <router-link :to="backLink" class="back-link mr-2">
                <i class="fas fa-arrow-left"></i>
              </router-link>
            </div>
            <router-link v-if="isOwnProfile" to="/profile" class="btn btn-sm btn-outline-gold">
              <i class="fas fa-edit mr-1"></i> Edit Profile
            </router-link>
          </h5>

          <div class="card-body">
            <div v-if="loading" class="text-center py-5">
              <b-spinner class="align-middle gold"></b-spinner>
              <p class="mt-2">Loading profile...</p>
            </div>

            <div v-else-if="profile" class="user-profile-content">
              <div class="profile-container">
                <div class="row" style="gap: 20px">
                  <!-- Left Side: Image and Chat Button -->
                  <div class="col-sm-auto image-container d-flex flex-column align-items-center p-0">
                    <img
                      v-if="!profile.profile_image"
                      class="profile-image"
                      src="https://media.iamconnected.com/profile_images/default.png"
                      alt="Profile Picture"
                    />
                    <img
                      v-else
                      class="profile-image"
                      :src="profile.profile_image"
                      alt="Profile Picture"
                    />

                    <!-- Peer Support Availability Badge -->
                    <div class="availability-badge mt-3 mb-2" :class="availabilityClass">
                      <i class="fas" :class="availabilityIcon"></i>
                      <span>{{ availabilityStatus }}</span>
                    </div>

                    <!-- Action Buttons (hide for own profile) -->
                    <div v-if="!isOwnProfile" class="d-flex flex-column" style="gap: 8px; width: 100%">
                      <router-link :to="`/chatbox/${profile.id}`" style="text-decoration: none">
                        <b-button class="btn-block" style="line-height: 1">Chat now</b-button>
                      </router-link>

                      <!-- Connect: none / not set -->
                      <b-button
                        v-if="!profile.connection_status || profile.connection_status === 'none'"
                        class="btn-block btn-connect"
                        style="line-height: 1"
                        @click="connectRequest"
                      >
                        <i class="fas fa-user-plus mr-1"></i> Connect
                      </b-button>

                      <!-- Connect: pending outgoing -->
                      <div
                        v-else-if="profile.connection_status === 'pending'"
                        class="connection-status-badge"
                      >
                        <i class="fas fa-clock mr-1"></i> Pending
                      </div>

                      <!-- Connect: pending incoming -->
                      <template v-else-if="profile.connection_status === 'pending_incoming'">
                        <b-button
                          class="btn-block btn-connect"
                          style="line-height: 1"
                          @click="acceptRequest"
                        >
                          <i class="fas fa-check mr-1"></i> Accept
                        </b-button>
                        <b-button
                          class="btn-block"
                          style="line-height: 1"
                          variant="outline-secondary"
                          @click="declineRequest"
                        >
                          <i class="fas fa-times mr-1"></i> Decline
                        </b-button>
                      </template>

                      <!-- Connect: connected -->
                      <div
                        v-else-if="profile.connection_status === 'connected'"
                        class="connection-status-badge"
                      >
                        <i class="fas fa-link mr-1"></i> Connected
                      </div>
                    </div>
                  </div>

                  <!-- Right Side: Profile Info -->
                  <div class="col info-container p-0">
                    <h2 class="name">{{ displayName }}</h2>

                    <!-- Location -->
                    <div v-if="locationString" class="info-row mb-2">
                      <i class="fas fa-map-marker-alt text-muted mr-2"></i>
                      <span>{{ locationString }}</span>
                    </div>

                    <!-- Local Time -->
                    <div v-if="currentLocalTime" class="info-row mb-3">
                      <i class="fas fa-clock text-muted mr-2"></i>
                      <span>Local time: {{ currentLocalTime }}</span>
                    </div>

                    <!-- Summary / About -->
                    <div v-if="profile.summary && profile.summary !== 'null'">
                      <p>{{ profile.summary }}</p>
                    </div>

                    <!-- Schedule -->
                    <div v-if="hasSchedule">
                      <h3 class="heading">Availability Schedule</h3>
                      <div class="schedule-list">
                        <div
                          v-for="(times, day) in formattedSchedule"
                          :key="day"
                          class="schedule-item"
                        >
                          <span class="day-name">{{ day }}</span>
                          <span class="day-times">{{ times }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-5 text-muted">
              <i class="fas fa-user-slash fa-2x mb-2"></i>
              <p>Profile not available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "UserProfile",
  data() {
    return {
      loading: false,
      profile: null,
    };
  },
  computed: {
    userId() {
      return this.$route.params.user_id;
    },
    isOwnProfile() {
      return this.$store.state.profile?.id == this.userId;
    },
    backLink() {
      // Return to the previous page or programs list
      const from = this.$route.query.from;
      if (from) {
        return from;
      }
      return "/programs";
    },
    displayName() {
      if (!this.profile) return "User Profile";
      if (this.profile.user_name) return this.profile.user_name;
      return `${this.profile.first_name} ${this.profile.last_name}`;
    },
    locationString() {
      if (!this.profile) return null;
      const parts = [];
      if (this.profile.city) parts.push(this.profile.city);
      if (this.profile.country) parts.push(this.profile.country);
      return parts.length > 0 ? parts.join(", ") : null;
    },
    currentLocalTime() {
      if (!this.profile || this.profile.timezoneOffsetH === null) return null;

      const now = new Date();
      const utc = now.getTime() + now.getTimezoneOffset() * 60000;
      const userTime = new Date(utc + this.profile.timezoneOffsetH * 3600000);

      return userTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    isCurrentlyAvailable() {
      return this.profile && this.profile.is_available === true;
    },
    availabilityStatus() {
      return this.isCurrentlyAvailable ? "Available" : "Not available right now";
    },
    availabilityClass() {
      return this.isCurrentlyAvailable ? "availability-available" : "availability-unavailable";
    },
    availabilityIcon() {
      return this.isCurrentlyAvailable ? "fa-check-circle" : "fa-times-circle";
    },
    authUserTimezone() {
      return this.$store.state.profile?.timezoneOffsetH || 0;
    },
    memberTimezone() {
      return this.profile?.timezoneOffsetH || 0;
    },
    timezoneOffset() {
      return this.authUserTimezone - this.memberTimezone;
    },
    hasSchedule() {
      if (!this.profile || !this.profile.peer_support_schedule) return false;
      const schedule = this.profile.peer_support_schedule;
      if (typeof schedule !== "object" || schedule === null) return false;

      // Hide schedule when availability is paused
      if (schedule.available === false) return false;

      return Object.keys(this.formattedSchedule).length > 0;
    },
    formattedSchedule() {
      if (!this.profile || !this.profile.peer_support_schedule) {
        return {};
      }
      const schedule = this.profile.peer_support_schedule;
      const formatted = {};
      const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

      const now = new Date();
      const utc = now.getTime() + now.getTimezoneOffset() * 60000;
      const authUserTime = new Date(utc + this.authUserTimezone * 3600000);
      const currentDayIndex = authUserTime.getDay();
      const currentHours = authUserTime.getHours();
      const currentMinutes = authUserTime.getMinutes();

      const dayIndexMap = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };

      dayOrder.forEach((day) => {
        if (schedule[day] && schedule[day].enabled) {
          const start = schedule[day].start || "09:00";
          const end = schedule[day].end || "17:00";

          const convertedStart = this.convertTimeToAuthTimezone(start);
          const convertedEnd = this.convertTimeToAuthTimezone(end);

          const [startHours, startMinutes] = convertedStart.split(":").map(Number);
          const [endHours, endMinutes] = convertedEnd.split(":").map(Number);
          const startTotalMinutes = startHours * 60 + startMinutes;
          const endTotalMinutes = endHours * 60 + endMinutes;
          const currentTotalMinutes = currentHours * 60 + currentMinutes;

          const isOvernight = endTotalMinutes < startTotalMinutes;
          const dayIndex = dayIndexMap[day];
          const isToday = dayIndex === currentDayIndex;

          if (false && isToday) {
            if (isOvernight) {
              if (currentTotalMinutes >= endTotalMinutes && currentTotalMinutes < startTotalMinutes) {
                return;
              }
            } else {
              if (currentTotalMinutes >= endTotalMinutes) {
                return;
              }
            }
          }

          const dayName = day.charAt(0).toUpperCase() + day.slice(1);
          if (isOvernight) {
            formatted[dayName] = `${this.formatTime(convertedStart)} - ${this.formatTime(convertedEnd)}`;
          } else {
            formatted[dayName] = `${this.formatTime(convertedStart)} - ${this.formatTime(convertedEnd)}`;
          }
        }
      });

      return formatted;
    },
  },
  watch: {
    userId: {
      handler(newId) {
        if (newId) {
          this.fetchProfile(newId);
        }
      },
      immediate: true,
    },
  },
  methods: {
    fetchProfile(userId) {
      this.loading = true;
      this.profile = null;

      const params = new URLSearchParams();
      params.append("token", this.$store.state.session_id);
      params.append("user_id", userId);

      axios({
        url: this.$store.state.api_url + "userPublicProfile",
        params: params,
      })
        .then((response) => {
          if (response.data.status === 1 && response.data.profile) {
            this.profile = response.data.profile;
          }
        })
        .catch((error) => {
          console.error("Error fetching profile:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatTime(time) {
      if (!time) return "";
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    },
    async connectRequest() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Send a connection request to ${this.displayName}?`,
        { title: 'Connect', okTitle: 'Send Request', cancelTitle: 'Cancel', centered: true }
      );
      if (!confirmed) return;

      this.$set(this.profile, 'connection_status', 'pending');
      try {
        await axios({
          method: 'post',
          url: 'connections',
          data: { target_user_id: this.profile.id },
        });
      } catch (err) {
        console.error('Connection request error:', err);
        this.$set(this.profile, 'connection_status', 'none');
      }
    },

    async acceptRequest() {
      this.$set(this.profile, 'connection_status', 'connected');
      try {
        await axios({
          url: this.$store.state.api_url + 'connectionAccept',
          params: { token: this.$store.state.session_id, sender_id: this.profile.id },
        });
      } catch (err) {
        console.error('Accept error:', err);
        this.$set(this.profile, 'connection_status', 'pending_incoming');
      }
    },

    async declineRequest() {
      this.$set(this.profile, 'connection_status', 'none');
      try {
        await axios({
          url: this.$store.state.api_url + 'connectionDecline',
          params: { token: this.$store.state.session_id, sender_id: this.profile.id },
        });
      } catch (err) {
        console.error('Decline error:', err);
        this.$set(this.profile, 'connection_status', 'pending_incoming');
      }
    },

    convertTimeToAuthTimezone(time) {
      if (!time) return time;

      const [hours, minutes] = time.split(":").map(Number);
      let newHours = hours + this.timezoneOffset;

      if (newHours >= 24) {
        newHours = newHours - 24;
      } else if (newHours < 0) {
        newHours = newHours + 24;
      }

      return `${String(newHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    },
  },
};
</script>

<style scoped>
#user-profile-page .card-header {
  display: flex;
  align-items: center;
}

.back-link {
  color: #d6b674;
  text-decoration: none;
}

.back-link:hover {
  color: #b89a5a;
}

.user-profile-content {
  padding: 10px;
}

.profile-container {
  padding: 10px;
}

.image-container {
  min-width: 185px;
}

.profile-image {
  max-width: 185px;
  width: 185px;
  height: 185px;
  object-fit: cover;
  padding: 10px;
  padding-top: 5px;
  padding-right: 0px;
  border-radius: 8px;
}

.info-container {
  padding-left: 10px !important;
}

.name {
  color: #d6b674;
  margin-bottom: 10px;
}

.heading {
  font-size: 115%;
  color: #d6b674;
  margin-top: 15px;
  margin-bottom: 8px;
}

.info-row {
  color: #666;
  font-size: 0.95rem;
}

.availability-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
}

.availability-available {
  background: linear-gradient(
    135deg,
    rgba(40, 167, 69, 0.15) 0%,
    rgba(40, 167, 69, 0.1) 100%
  );
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.availability-unavailable {
  background: linear-gradient(
    135deg,
    rgba(108, 117, 125, 0.15) 0%,
    rgba(108, 117, 125, 0.1) 100%
  );
  color: #6c757d;
  border: 1px solid rgba(108, 117, 125, 0.3);
}

.schedule-list {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 10px 12px;
}

.schedule-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid #eee;
  font-size: 0.9rem;
}

.schedule-item:last-child {
  border-bottom: none;
}

.day-name {
  font-weight: 500;
  color: #333;
}

.day-times {
  color: #666;
}

.gold {
  color: #d6b674;
}

/* Connection status badge */
.connection-status-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  background: linear-gradient(135deg, rgba(214,182,116,0.15) 0%, rgba(196,165,90,0.15) 100%);
  color: #c4a55a;
  border: 1px solid rgba(214,182,116,0.45);
}

/* Connect Button */
.btn-connect {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%) !important;
  border: none !important;
  color: #fff !important;
  font-weight: 600;
  border-radius: 6px;
  transition: opacity 0.18s;
}

.btn-connect:hover {
  opacity: 0.88;
  color: #fff !important;
}

/* Edit Profile Button */
.btn-outline-gold {
  color: #d6b674;
  border-color: #d6b674;
  background: transparent;
}

.btn-outline-gold:hover {
  color: #fff;
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  border-color: #d6b674;
}

@media (max-width: 576px) {
  .profile-container {
    margin-left: 0;
    margin-right: 0;
  }

  .image-container {
    width: 100%;
    margin-bottom: 15px;
  }

  .profile-image {
    max-width: 150px;
    width: 150px;
    height: 150px;
  }
}
</style>
